import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  Alert,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';
import { FEATURE_FLAG_NAMES } from '../../constants/featureFlags';

function UserFeatureFlagsCard({ userId }) {
  const { showSuccess, showError } = useToast();
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingFlags, setLoadingFlags] = useState({});
  const [addFlagModalOpen, setAddFlagModalOpen] = useState(false);
  const [addFlagForm, setAddFlagForm] = useState({
    featureName: '',
    featureValue: false
  });
  const [addFlagLoading, setAddFlagLoading] = useState(false);
  const [deleteFlagModalOpen, setDeleteFlagModalOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState(null);
  const [deleteFlagLoading, setDeleteFlagLoading] = useState(false);
  const [featureFlagConfirmModalOpen, setFeatureFlagConfirmModalOpen] = useState(false);
  const [featureFlagToToggle, setFeatureFlagToToggle] = useState(null);
  const [featureFlagToggleLoading, setFeatureFlagToggleLoading] = useState(false);

  useEffect(() => {
    fetchFeatureFlags();
  }, [userId]);

  const fetchFeatureFlags = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchUserFeatureFlags(userId);
      
      if (result.success) {
        setFeatureFlags(result.data || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load user feature flags');
      console.error('Failed to fetch user feature flags:', err);
    }
    
    setLoading(false);
  };

  const handleFeatureFlagToggle = (flagId, featureName, newValue) => {
    // Set the flag to toggle and show confirmation modal
    setFeatureFlagToToggle({
      flagId,
      featureName,
      newValue,
      currentValue: !newValue
    });
    setFeatureFlagConfirmModalOpen(true);
  };

  const handleFeatureFlagConfirm = async () => {
    if (!featureFlagToToggle) return;
    
    setFeatureFlagToggleLoading(true);
    
    const { flagId, featureName, newValue } = featureFlagToToggle;

    // Log the data being sent (as requested)
    console.log('User Feature Flag Toggle:', {
      feature_name: featureName,
      feature_value: newValue,
      user_id: userId
    });

    try {
      const result = await apiService.updateUserFeatureFlag(flagId, newValue);
      
      if (result.success) {
        console.log('User feature flag updated successfully:', result.data);
        // Update the feature flags list with the response data
        setFeatureFlags(prev => 
          prev.map(flag => 
            flag.unique_id === flagId 
              ? { 
                  ...flag, 
                  feature_value: result.data.updated_data.feature_value 
                }
              : flag
          )
        );
        showSuccess(`Feature flag "${featureName}" updated successfully`, 'Success');
      } else {
        console.error('Failed to update user feature flag:', result.error);
        showError(`Failed to update feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating user feature flag:', error);
      showError('Failed to update feature flag. Please try again.', 'Error');
    } finally {
      setFeatureFlagToggleLoading(false);
      setFeatureFlagConfirmModalOpen(false);
      setFeatureFlagToToggle(null);
    }
  };

  const handleFeatureFlagCancel = () => {
    setFeatureFlagConfirmModalOpen(false);
    setFeatureFlagToToggle(null);
  };

  const handleAddFlagClick = () => {
    setAddFlagForm({
      featureName: '',
      featureValue: false
    });
    setAddFlagModalOpen(true);
  };

  const handleAddFlagModalClose = () => {
    setAddFlagModalOpen(false);
  };

  const handleAddFlagFormChange = (field, value) => {
    setAddFlagForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFlagSubmit = async () => {
    if (!addFlagForm.featureName.trim()) {
      showError('Please enter a feature name', 'Validation Error');
      return;
    }

    setAddFlagLoading(true);

    try {
      const result = await apiService.createUserFeatureFlag(userId, {
        feature_name: addFlagForm.featureName.trim(),
        feature_value: addFlagForm.featureValue
      });

      if (result.success) {
        console.log('User feature flag created successfully:', result.data);
        // Add the new feature flag to the list
        setFeatureFlags(prev => [...prev, result.data.created_data]);
        setAddFlagModalOpen(false);
        setAddFlagForm({ featureName: '', featureValue: false });
        showSuccess(`Feature flag "${addFlagForm.featureName}" created successfully`, 'Success');
      } else {
        console.error('Failed to create user feature flag:', result.error);
        showError(`Failed to create feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error creating user feature flag:', error);
      showError('Failed to create feature flag. Please try again.', 'Error');
    } finally {
      setAddFlagLoading(false);
    }
  };

  const handleDeleteFlagClick = (flag) => {
    setFlagToDelete(flag);
    setDeleteFlagModalOpen(true);
  };

  const handleDeleteFlagModalClose = () => {
    setDeleteFlagModalOpen(false);
    setFlagToDelete(null);
  };

  const handleDeleteFlagConfirm = async () => {
    if (!flagToDelete) return;

    setDeleteFlagLoading(true);

    try {
      const result = await apiService.deleteUserFeatureFlag(flagToDelete.unique_id);

      if (result.success) {
        console.log('User feature flag deleted successfully:', result.data);
        // Remove the deleted feature flag from the list
        setFeatureFlags(prev => prev.filter(flag => flag.unique_id !== flagToDelete.unique_id));
        setDeleteFlagModalOpen(false);
        setFlagToDelete(null);
        showSuccess(`Feature flag "${flagToDelete.feature_name}" deleted successfully`, 'Success');
      } else {
        console.error('Failed to delete user feature flag:', result.error);
        showError(`Failed to delete feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting user feature flag:', error);
      showError('Failed to delete feature flag. Please try again.', 'Error');
    } finally {
      setDeleteFlagLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            🚩 User Feature Flags
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            🚩 User Feature Flags
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🚩 User Feature Flags
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleAddFlagClick}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'white',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <Add fontSize="small" />
          </IconButton>
        </Box>
        
        {featureFlags.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            px: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              No feature flags found for this user
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {featureFlags.map((flag) => (
              <Paper 
                key={flag.unique_id} 
                elevation={1}
                sx={{ 
                  p: 2.5, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      {flag.feature_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {flag.unique_id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={flag.feature_value}
                          onChange={(e) => handleFeatureFlagToggle(flag.unique_id, flag.feature_name, e.target.checked)}
                          color="primary"
                          disabled={loadingFlags[flag.unique_id]}
                          sx={{
                            '& .MuiSwitch-thumb': {
                              opacity: loadingFlags[flag.unique_id] ? 0.6 : 1,
                            },
                            '& .MuiSwitch-track': {
                              opacity: loadingFlags[flag.unique_id] ? 0.6 : 1,
                            }
                          }}
                        />
                      }
                      label=""
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFlagClick(flag)}
                      sx={{ 
                        color: 'error.main',
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'white',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  pt: 2, 
                  borderTop: '1px solid', 
                  borderColor: 'divider' 
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Current Value
                  </Typography>
                  <Chip 
                    label={flag.feature_value ? 'Enabled' : 'Disabled'} 
                    color={flag.feature_value ? 'success' : 'default'} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Add Feature Flag Modal */}
        <Modal
          open={addFlagModalOpen}
          onClose={handleAddFlagModalClose}
          aria-labelledby="add-user-feature-flag-modal"
          aria-describedby="add-user-feature-flag-form"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom>
              Add User Feature Flag
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth disabled={addFlagLoading}>
                <InputLabel>Feature Name</InputLabel>
                <Select
                  value={addFlagForm.featureName}
                  onChange={(e) => handleAddFlagFormChange('featureName', e.target.value)}
                  label="Feature Name"
                >
                  {FEATURE_FLAG_NAMES.map((flagName) => (
                    <MenuItem key={flagName} value={flagName}>
                      {flagName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={addFlagForm.featureValue}
                    onChange={(e) => handleAddFlagFormChange('featureValue', e.target.checked)}
                    color="primary"
                    disabled={addFlagLoading}
                  />
                }
                label="Feature Value (Enabled/Disabled)"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleAddFlagModalClose}
                disabled={addFlagLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleAddFlagSubmit}
                disabled={addFlagLoading || !addFlagForm.featureName.trim()}
                startIcon={addFlagLoading ? <CircularProgress size={16} /> : null}
              >
                {addFlagLoading ? 'Creating...' : 'Create'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteFlagModalOpen}
          onClose={handleDeleteFlagModalClose}
          aria-labelledby="delete-user-feature-flag-modal"
          aria-describedby="delete-user-feature-flag-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="error">
              Delete User Feature Flag
            </Typography>
            
            {flagToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to delete the feature flag:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  mb: 3
                }}>
                  <Typography variant="body1" fontWeight="medium">
                    {flagToDelete.feature_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {flagToDelete.unique_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Value: {flagToDelete.feature_value ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                  This action cannot be undone.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleDeleteFlagModalClose}
                disabled={deleteFlagLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleDeleteFlagConfirm}
                disabled={deleteFlagLoading}
                startIcon={deleteFlagLoading ? <CircularProgress size={16} /> : null}
              >
                {deleteFlagLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Feature Flag Toggle Confirmation Modal */}
        <Modal
          open={featureFlagConfirmModalOpen}
          onClose={handleFeatureFlagCancel}
          aria-labelledby="feature-flag-toggle-modal"
          aria-describedby="feature-flag-toggle-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Toggle User Feature Flag
            </Typography>
            
            {featureFlagToToggle && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Are you sure you want to change the user feature flag:
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'primary.light', 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'primary.main',
                  mb: 3
                }}>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
                    {featureFlagToToggle.featureName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ID: {featureFlagToToggle.flagId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Current Value: {featureFlagToToggle.currentValue ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight="600">
                    New Value: {featureFlagToToggle.newValue ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
                  This will immediately update the feature flag for this user.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleFeatureFlagCancel}
                disabled={featureFlagToggleLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleFeatureFlagConfirm}
                disabled={featureFlagToggleLoading}
                startIcon={featureFlagToggleLoading ? <CircularProgress size={16} /> : null}
              >
                {featureFlagToggleLoading ? 'Updating...' : 'Confirm'}
              </Button>
            </Box>
          </Box>
        </Modal>
      </CardContent>
    </Card>
  );
}

export default UserFeatureFlagsCard; 